/**
 * 라우터 설정
 */

import React, { Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import RequireAuth from './RequireAuth';
import RequireAdmin from './RequireAdmin';
import { lazyWithChunkRecovery } from '../lib/chunkRecovery';

const DashboardPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/DashboardPage')));
const ContentPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/ContentPage')));
const ApprovalPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/ApprovalPage')));
const ProjectsPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/ProjectsPage')));
const WorkflowPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/WorkflowPage')));
const SettingsPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/SettingsPage')));
const CampaignPlannerPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/CampaignPlannerPage')));
const FrameworksPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/FrameworksPage')));
const RevenueOpsPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/RevenueOpsPage')));
const BoothIntakePage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/BoothIntakePage')));
const EventInquiryPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/EventInquiryPage')));
const SeriesPlannerPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/SeriesPlannerPage')));
const RiskReviewPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/RiskReviewPage')));
const VideoPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/VideoPage')));
const ProductStudioPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/ProductStudioPage')));
const SourceLibraryPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/SourceLibraryPage')));
const PublishingBoardPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/PublishingBoardPage')));
const SignupPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/SignupPage')));
const LoginPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/LoginPage')));
const VerifyEmailPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/VerifyEmailPage')));
const ForgotPasswordPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/ForgotPasswordPage')));
const ResetPasswordPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/ResetPasswordPage')));
const LandingPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/LandingPage')));
const BillingCheckoutPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/BillingCheckoutPage')));
const InquiryPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/InquiryPage')));
const OnboardingPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/OnboardingPage')));
const NotFoundPage = React.lazy(() => lazyWithChunkRecovery(() => import('../pages/NotFoundPage')));

const PageFallback = () => (
  <div style={{ padding: 32, color: '#475569', fontSize: 14 }}>화면을 불러오는 중입니다.</div>
);

const withSuspense = (page: React.ReactElement) => (
  <Suspense fallback={<PageFallback />}>{page}</Suspense>
);

export const routes: RouteObject[] = [
  {
    path: '/',
    element: withSuspense(<LandingPage />),
  },
  {
    path: '/login',
    element: withSuspense(<LoginPage />),
  },
  {
    path: '/signup',
    element: withSuspense(<SignupPage />),
  },
  {
    path: '/verify-email',
    element: withSuspense(<VerifyEmailPage />),
  },
  {
    path: '/forgot-password',
    element: withSuspense(<ForgotPasswordPage />),
  },
  {
    path: '/reset-password',
    element: withSuspense(<ResetPasswordPage />),
  },
  {
    path: '/inquiry',
    element: withSuspense(<InquiryPage />),
  },
  {
    path: '/app',
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: withSuspense(<DashboardPage />),
      },
      {
        path: 'onboarding',
        element: withSuspense(<OnboardingPage />),
      },
      {
        path: 'dashboard',
        element: withSuspense(<DashboardPage />),
      },
      {
        path: 'content',
        element: withSuspense(<ContentPage />),
      },
      {
        path: 'approval',
        element: withSuspense(<ApprovalPage />),
      },
      {
        path: 'workflow',
        element: withSuspense(<WorkflowPage />),
      },
      {
        path: 'projects',
        element: withSuspense(<ProjectsPage />),
      },
      {
        path: 'projects/:projectId',
        element: withSuspense(<DashboardPage />),
      },
      {
        path: 'campaign-planner',
        element: withSuspense(<CampaignPlannerPage />),
      },
      {
        path: 'frameworks',
        element: withSuspense(<FrameworksPage />),
      },
      {
        path: 'booth-intake',
        element: <RequireAdmin>{withSuspense(<BoothIntakePage />)}</RequireAdmin>,
      },
      {
        path: 'revenue-ops',
        element: <RequireAdmin>{withSuspense(<RevenueOpsPage />)}</RequireAdmin>,
      },
      {
        path: 'series-planner',
        element: withSuspense(<SeriesPlannerPage />),
      },
      {
        path: 'risk-review',
        element: withSuspense(<RiskReviewPage />),
      },
      {
        path: 'video',
        element: withSuspense(<VideoPage />),
      },
      {
        path: 'product-studio',
        element: withSuspense(<ProductStudioPage />),
      },
      {
        path: 'source-library',
        element: withSuspense(<SourceLibraryPage />),
      },
      {
        path: 'publishing-board',
        element: withSuspense(<PublishingBoardPage />),
      },
      {
        path: 'settings',
        element: withSuspense(<SettingsPage />),
      },
      {
        path: 'checkout',
        element: withSuspense(<BillingCheckoutPage />),
      },
      {
        path: '*',
        element: withSuspense(<NotFoundPage />),
      },
    ],
  },
  {
    path: '/dashboard',
    element: <Navigate to="/app/dashboard" replace />,
  },
  {
    path: '/content',
    element: <Navigate to="/app/content" replace />,
  },
  {
    path: '/approval',
    element: <Navigate to="/app/approval" replace />,
  },
  {
    path: '/workflow',
    element: <Navigate to="/app/workflow" replace />,
  },
  {
    path: '/frameworks',
    element: <Navigate to="/app/frameworks" replace />,
  },
  {
    path: '/revenue-ops',
    element: <Navigate to="/app/revenue-ops" replace />,
  },
  {
    path: '/projects',
    element: <Navigate to="/app/projects" replace />,
  },
  {
    path: '/settings',
    element: <Navigate to="/app/settings" replace />,
  },
  {
    path: '/purchase',
    element: <Navigate to="/app/checkout" replace />,
  },
  {
    path: '/product-studio',
    element: <Navigate to="/app/product-studio" replace />,
  },
  {
    // 전시 QR 공개 문의 — 로그인 없음. 서버 기능 플래그가 꺼져 있으면 폼 대신 «현장 문의» 안내만 보인다
    path: '/event/:eventCode/:brand',
    element: withSuspense(<EventInquiryPage />),
  },
  {
    path: '*',
    element: withSuspense(<NotFoundPage />),
  },
];
